import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RangeType } from '@constants/pages/user-defined-page/range-type';
import { UserDefinedWidgetType } from '@constants/pages/user-defined-page/user-defined-widget-type';
import { Signal } from '@models/dashboard/signal';
import { Axis } from '@models/pages/interactive-data-analysis/interactive-data-analysis-signal-chart-properties';
import { ChartAxisSettings } from '@models/pages/user-defined-page/chart-axis-settings';
import { UserDefinedWidget } from '@models/pages/user-defined-page/user-defined-widget';
import { TranslateService } from '@ngx-translate/core';
import { ChartsConfigurationHelperService } from '@services/charts/helper/charts-configuration-helper.service';

@Component({
    selector: 'scitis-line-widget-settings',
    templateUrl: './line-widget-settings.component.html',
    styleUrls: ['./line-widget-settings.component.scss'],
    standalone: false
})
export class LineWidgetSettingsComponent implements OnInit {

  @Input()
  userDefinedWidget: UserDefinedWidget = new UserDefinedWidget();

  @Input()
  machineSignals: Signal[] = [];

  @Input()
  isTemplateWidget: boolean = false;

  @Output()
  isConfigurationValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  usableSignals: Signal[] = [];
  usedSignalIds: number[] = [];
  validTimePeriod = false;
  hasDeletedSignals: boolean = false;

  public selectedAxis: Map<string, boolean> = new Map<string, boolean>();

  private AXIS_ID_PREFIX: string = 'axis_';

  constructor(private translate: TranslateService,
              private chartsConfigurationHelperService: ChartsConfigurationHelperService) {
  }

  public get isLineWidget(): boolean {
    return this.userDefinedWidget.widgetDetails.widgetType === UserDefinedWidgetType.LINE_WITH_SIGNALS_PER_AXIS;
  }

  ngOnInit(): void {
    this.chartsConfigurationHelperService.hasDeletedSignals$.subscribe(hasDeletedSignals => {
      this.hasDeletedSignals = Array.from(hasDeletedSignals.values()).some(val => val === true);
    });
    this.usableSignals = [...this.chartsConfigurationHelperService.filterMisconfiguredOrUnusableSignals(this.machineSignals)];
    this.usedSignalIds = this.getUsedSignals(this.userDefinedWidget.userDefinedChartDetails.axis);
    this.emitValidConfigurationEvent();
  }

  onTimePeriodChange({ timeValue, rangeType }: {timeValue: number, rangeType: RangeType}) {
    this.userDefinedWidget.userDefinedChartDetails.rangeValue = timeValue;
    this.userDefinedWidget.userDefinedChartDetails.rangeType = rangeType;
    this.emitValidConfigurationEvent();
  }

  public onAddNewAxis() {
    const axis: ChartAxisSettings = new ChartAxisSettings();
    axis.id = this.AXIS_ID_PREFIX + new Date().getTime();
    axis.axisYLabel = this.translate.instant('New Axis');
    axis.minY = 0;
    axis.maxY = 0;
    axis.position = Axis.LEFT;
    axis.signals = [];

    if (!this.userDefinedWidget.userDefinedChartDetails.axis) {
      this.userDefinedWidget.userDefinedChartDetails.axis = [];
    }
    this.userDefinedWidget.userDefinedChartDetails.axis = [...this.userDefinedWidget.userDefinedChartDetails.axis, axis];
    this.setExpandedAxis(axis.id);
    this.emitValidConfigurationEvent();
  }

  onAxisStartEdit($event: string) {
    this.setExpandedAxis($event);
  }

  onAxisDelete($event: ChartAxisSettings) {
    this.userDefinedWidget.userDefinedChartDetails.axis.splice(this.userDefinedWidget.userDefinedChartDetails.axis.findIndex(a => a.id === $event.id), 1);
    this.usedSignalIds = this.getUsedSignals(this.userDefinedWidget.userDefinedChartDetails.axis);
    this.emitValidConfigurationEvent();
  }

  onAxisSettingsChanged($event: ChartAxisSettings) {
    const axisIndex = this.userDefinedWidget.userDefinedChartDetails.axis.findIndex(a => a.id === $event.id);
    this.userDefinedWidget.userDefinedChartDetails.axis[axisIndex] = $event;
    this.usedSignalIds = this.getUsedSignals(this.userDefinedWidget.userDefinedChartDetails.axis);
    this.emitValidConfigurationEvent();
  }

  onValidTimePeriodChanged(event: boolean) {
    this.validTimePeriod = event;
    this.emitValidConfigurationEvent();
  }

  private setExpandedAxis(id: string) {
    for (const axis of this.userDefinedWidget.userDefinedChartDetails.axis) {
      if (axis.id && axis.id === id) {
        this.selectedAxis.set(axis.id, true);
      } else {
        this.selectedAxis.set(axis.id, false);
      }
    }
  }

  private emitValidConfigurationEvent() {
    const validConfiguration =
      this.chartsConfigurationHelperService.validateLineWidgetConfiguration(this.userDefinedWidget) && this.validTimePeriod;
    this.isConfigurationValid.emit(validConfiguration && !this.hasDeletedSignals);
  }

  private getUsedSignals(axis: ChartAxisSettings[]): number[] {

    if (!this.isTemplateWidget) {
      return [...this.chartsConfigurationHelperService.getUsedSignalsIdsOnAllAxis(axis)];
    }
    const usedSignalsNameOnAllAxis = this.chartsConfigurationHelperService.getUsedSignalsNameOnAllAxis(axis).map(signalName => {
      return { signalName };
    });
    return this.chartsConfigurationHelperService.getSelectedSignalIds(usedSignalsNameOnAllAxis, this.machineSignals);
  }


}
